/**
 * Bo'limlar fonidagi 3D sahnalar.
 *
 * `SceneCell` — bo'lim ichidagi joy (2D kanvas, sarlavhaning bir ustuni).
 * `SectionScenes` — sahifada bir marta: three.js va sahna kodini brauzer
 * bo'shaganda dinamik yuklaydi va barcha anchor'larga bitta WebGL renderer
 * orqali chizadi (`stage.ts`).
 *
 * Ishga tushmaydi: telefon/planshet (≤ 900 px — joy yo'q, quvvat tejaladi),
 * yuqori kontrast mavzusi, WebGL2 bo'lmagan brauzer. Bunday holda anchor'lar
 * bo'sh qoladi va sahifa avvalgidek ko'rinadi.
 */
import { useEffect } from "react";

import { prefersReducedMotion } from "@/shared/lib/motion";
import { usePrefs } from "@/shared/lib/prefs";

import type { StageHandle } from "./stage";

/**
 * Sahna joyi: bo'lim sarlavhasining bir ustuni (`SplitHead`) yoki kartochka
 * ichidagi joy (mobil ilova). Sahnalar faol bo'lmasa (`html.has-scenes` yo'q)
 * ko'rinmaydi — sarlavha bitta ustunda qoladi.
 */
export function SceneCell({ name, className = "" }: { name: string; className?: string }) {
  return (
    <div className={`scene-cell ${className}`.trim()} aria-hidden="true">
      <canvas className="scene-anchor" data-scene={name} />
    </div>
  );
}

function supportsWebGL2(): boolean {
  try {
    return Boolean(document.createElement("canvas").getContext("webgl2"));
  } catch {
    return false;
  }
}

export function SectionScenes() {
  const { theme } = usePrefs();

  useEffect(() => {
    if (theme === "hc" || !window.matchMedia("(min-width: 901px)").matches || !supportsWebGL2()) return;

    let disposed = false;
    let handle: StageHandle | null = null;
    const start = () => {
      import("./stage")
        .then(({ createStage }) => {
          if (!disposed) handle = createStage({ reducedMotion: prefersReducedMotion() });
        })
        .catch((error: unknown) => console.warn("Bo'lim 3D sahnalari yuklanmadi:", error));
    };
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(start, { timeout: 2500 })
      : window.setTimeout(start, 800);

    return () => {
      disposed = true;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      handle?.dispose();
    };
  }, [theme]);

  return null;
}
